import React from 'react'

type Props = {
  data: {name: string; current: number | string; previous: number | string}[]
  year1: number
  year2: number
  isCurrency?: boolean
}

const numeric = (value: number | string) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const SimpleBarChart: React.FC<Props> = ({data, year1, year2, isCurrency = false}) => {
  if (!data?.length) return null
  const maximum = Math.max(
    0,
    ...data.flatMap((item) => [numeric(item.current), numeric(item.previous)])
  )
  const formatter = new Intl.NumberFormat('bs-BA', {
    minimumFractionDigits: isCurrency ? 2 : 0,
    maximumFractionDigits: isCurrency ? 2 : 0,
  })
  const format = (value: number) => `${formatter.format(value)}${isCurrency ? ' KM' : ''}`
  const series = [
    {key: 'current' as const, year: year1, color: '#2563eb'},
    {key: 'previous' as const, year: year2, color: '#7c3aed'},
  ]

  return (
    <div>
      <div className='d-flex flex-wrap gap-5 mb-6'>
        {series.map(({key, year, color}) => (
          <div key={key} className='d-flex align-items-center gap-2 fw-semibold fs-7'>
            <span
              className='rounded'
              style={{width: 12, height: 12, backgroundColor: color}}
              aria-hidden='true'
            />
            {year}. godina
          </div>
        ))}
      </div>
      <div className='d-flex flex-column gap-7'>
        {data.map((item) => (
          <div key={item.name}>
            <div className='fw-bold text-gray-900 mb-3'>{item.name}</div>
            {series.map(({key, year, color}) => {
              const amount = numeric(item[key])
              const width = maximum > 0 ? (Math.max(0, amount) / maximum) * 100 : 0
              return (
                <div key={key} className='mb-4'>
                  <div className='d-flex flex-wrap justify-content-between gap-2 fs-7 mb-2'>
                    <span className='text-gray-700'>{year}.</span>
                    <span className='fw-bold text-gray-900'>{format(amount)}</span>
                  </div>
                  <div
                    className='bg-light rounded overflow-hidden'
                    style={{height: 22}}
                    aria-hidden='true'
                  >
                    <div
                      className='h-100 rounded'
                      style={{width: `${width}%`, backgroundColor: color}}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        ))}
      </div>
      <div
        className='d-flex justify-content-between border-top pt-3 text-gray-700 fs-8'
        aria-hidden='true'
      >
        <span>0</span>
        <span>{format(maximum)}</span>
      </div>
      {maximum === 0 && <p className='text-muted fs-7 mt-3 mb-0'>Nema vrijednosti za prikaz.</p>}
    </div>
  )
}

export default SimpleBarChart
